import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WO_STATUS_LABELS } from "@/pages/MisTrabajos";

const EDITABLE = ["pending", "in_progress", "paused", "completed"];

function MyWorkOrder({ wo, onChanged }) {
  const [hours, setHours] = useState(wo.actual_hours ?? "");
  const [notes, setNotes] = useState(wo.technician_notes || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/my/work-orders/${wo.id}`, body);
      toast.success("Guardado");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const saveHoursNotes = () => {
    const raw = String(hours).trim();
    const h = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isFinite(h) || h < 0 || h > 1000)) return toast.error("Horas reales: número entre 0 y 1000");
    patch({ ...(h !== null ? { actual_hours: h } : {}), technician_notes: notes });
  };
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("evidence_type", "photo");
    fd.append("title", file.name);
    setBusy(true);
    try {
      await api.post(`/my/work-orders/${wo.id}/evidences`, fd);
      toast.success("Evidencia cargada (interna)");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 border-border space-y-3" data-testid={`my-wo-detail-${wo.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium">{wo.title}</div>
        <Select value={wo.work_status} onValueChange={(v) => patch({ work_status: v })} disabled={busy || !EDITABLE.includes(wo.work_status)}>
          <SelectTrigger className="w-40 h-8" data-testid={`my-wo-status-${wo.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{EDITABLE.map((s) => <SelectItem key={s} value={s}>{WO_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {wo.description && <p className="text-sm text-slate-700 whitespace-pre-line" data-testid={`my-wo-instructions-${wo.id}`}>{wo.description}</p>}
      <div className="space-y-1">
        {wo.tasks.map((t) => (
          <label key={t.id} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!t.is_completed} disabled={busy}
              onChange={(e) => api.patch(`/my/work-orders/${wo.id}/tasks/${t.id}`, { is_completed: e.target.checked }).then(onChanged).catch((err) => toast.error(formatApiError(err)))}
              data-testid={`my-task-${t.id}`} />
            {t.title}
          </label>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
        <Input type="number" step="0.1" min="0" inputMode="decimal" placeholder="Horas reales" value={hours} onChange={(e) => setHours(e.target.value)} data-testid={`my-wo-hours-${wo.id}`} />
        <Textarea rows={2} placeholder="Notas técnicas" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`my-wo-notes-${wo.id}`} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={saveHoursNotes} disabled={busy} data-testid={`my-wo-save-${wo.id}`}>Guardar horas y notas</Button>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`my-wo-upload-${wo.id}`}>Cargar evidencia</Button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </div>
    </Card>
  );
}

export default function MiOrden() {
  const { soId } = useParams();
  const [so, setSo] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get(`/my/service-orders/${soId}`).then(({ data }) => { setSo(data); setError(null); })
      .catch((e) => setError(formatApiError(e)));
  }, [soId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="space-y-3" data-testid="my-so-error"><p className="text-sm">{error}</p><Link className="text-sm underline" to="/mis-trabajos">Volver a mis trabajos</Link></div>;
  if (!so) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  const v = so.vehicle || {};
  return (
    <div className="space-y-6" data-testid="page-mi-orden">
      <PageHeader title={so.folio} description={[v.brand, v.model, v.year, v.plates, v.color].filter(Boolean).join(" · ")} />
      <Card className="p-4 border-border space-y-1 text-sm">
        {so.stage_name && <div data-testid="my-so-stage"><b>{so.service_flow_name}</b> · {so.stage_name}</div>}
        <div><span className="text-muted-foreground">Motivo de ingreso:</span> {so.customer_reported_issue || "—"}</div>
      </Card>
      {so.work_orders.map((wo) => <MyWorkOrder key={wo.id} wo={wo} onChanged={load} />)}
      {so.evidences.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {so.evidences.map((e) => <AuthImage key={e.id} path={`/my/evidences/${e.id}/file`} alt={e.title || ""} className="w-full h-32 object-cover rounded-md" testId={`my-evidence-${e.id}`} />)}
        </div>
      )}
    </div>
  );
}
